import React, { useState } from "react";
import { FaPen, FaLock, FaTrash, FaUser, FaUnlock, FaUsers, FaPlus, FaChair, FaUserFriends } from 'react-icons/fa';
import { useDispatch } from "react-redux";
import ConfirmModal from "./ConfirmModal";
import { refreshDataGuest } from '../store/guestSlice'
import AdminService from "../services/adminService";
import EditTableModal from "./EditTableModal";
import { useTranslation } from "react-i18next";

const pluralizeOsoba = (n) => {
    if (n === 1) return 'osoba';
    if (n >= 2 && n <= 4) return 'osobe';
    return 'osoba';
};

const Tables = ({ tables, setActiveForm, setShowNotification, setNotificationMessage, setErrorResponse }) => {
    const { t } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTableId, setSelectedTableId] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedTable, setSelectedTable] = useState(null);
    const [actionType, setActionType] = useState('');
    const [message, setMessage] = useState('');
    const [activeTab, setActiveTab] = useState("public");

    const dispatch = useDispatch();

    const capitalizeWords = (str) =>
        str?.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

    const handleDeleteTable = (tableId) => {
        const table = tables.find(table => table._id === tableId);
        setSelectedTableId(tableId);
        setActionType('delete');
        setMessage(t('confirmDeleteTable', { tableName: table.name }));
        setIsModalOpen(true);
    };

    const handleLockTable = (tableId) => {
        const table = tables.find(table => table._id === tableId);
        setSelectedTableId(tableId);
        setActionType('lock');
        const action = table.isLocked ? t('unlock') : t('lock');
        setMessage(t('confirmLockUnlockTable', { action, tableName: table.name }));
        setIsModalOpen(true);
    };

    const handleEditTable = (table) => {
        setSelectedTable(table);
        setIsEditModalOpen(true);
    };

    const handleConfirmAction = () => {
        setIsModalOpen(false);

        if (actionType === 'delete') {
            AdminService.deleteTable(selectedTableId)
                .then(() => {
                    dispatch(refreshDataGuest());
                    setNotificationMessage(t('tableDeletedSuccess'));
                    setShowNotification(true);
                    setTimeout(() => setShowNotification(false), 3000);
                })
                .catch(() => {
                    setErrorResponse(true);
                    setTimeout(() => setErrorResponse(false), 3000);
                });
        } else if (actionType === 'lock') {
            const table = tables.find(table => table._id === selectedTableId);
            const updatedLockState = !table.isLocked;

            AdminService.updateTable(selectedTableId, {
                name: table.name,
                seats: table.seats,
                isLocked: updatedLockState
            })
                .then(() => {
                    dispatch(refreshDataGuest());
                    setNotificationMessage(t('tableLockSuccess', {
                        state: updatedLockState ? t('locked') : t('unlocked')
                    }));
                    setShowNotification(true);
                    setTimeout(() => setShowNotification(false), 3000);
                })
                .catch(() => {
                    setErrorResponse(true);
                    setTimeout(() => setErrorResponse(false), 3000);
                });
        }

        setSelectedTableId(null);
        setActionType('');
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedTableId(null);
        setActionType('');
        setMessage('');
    };

    const publicTables = tables.filter(table => !table.isPrivateTable);
    const privateTables = tables.filter(table => table.isPrivateTable);

    const getFillPercent = (table) =>
        table.seats > 0 ? Math.round((table.reservedSeats / table.seats) * 100) : 0;

    const TableCard = ({ table }) => {
        const fillPercent = getFillPercent(table);
        const freeSeats = table.seats - table.reservedSeats;
        const isFull = freeSeats === 0;

        return (
            <div className={`flex flex-col bg-white border rounded-2xl shadow-sm hover:shadow-md transition-all overflow-hidden
                ${table.isLocked ? 'border-red-200' : isFull ? 'border-green-300' : 'border-gray-200'}`}>

                {/* Header */}
                <div className={`px-4 py-3 flex items-center justify-between
                    ${table.isLocked ? 'bg-red-50' : isFull ? 'bg-green-50' : 'bg-gray-50'}
                    border-b ${table.isLocked ? 'border-red-100' : isFull ? 'border-green-100' : 'border-gray-100'}`}>
                    <div className="flex items-center gap-2 min-w-0">
                        <h2 className="text-lg font-bold text-gray-800 capitalize truncate">
                            {table.name}
                        </h2>
                        {table.isPrivateTable && (
                            <span className="text-xs bg-rose-100 text-rose-500 border border-rose-200 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                                {t('privateTable')}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                        {table.isLocked && !isFull && (
                            <span className="text-xs bg-red-100 text-red-500 border border-red-200 px-2 py-0.5 rounded-full font-medium">
                                {t('locked')}
                            </span>
                        )}
                        {isFull && (
                            <span className="text-xs bg-green-100 text-green-700 border border-green-200 px-2 py-0.5 rounded-full font-medium">
                                {t('tableFull')}
                            </span>
                        )}
                    </div>
                </div>

                {/* Capacity bar */}
                <div className="px-4 pt-3 pb-2">
                    <div className="flex items-center justify-between text-sm mb-1.5">
                        <span className="flex items-center gap-1.5 text-gray-600 font-medium">
                            <FaChair className="text-gray-400 text-xs" />
                            {table.reservedSeats} / {table.seats}
                        </span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full
                            ${isFull ? 'bg-green-100 text-green-700' : freeSeats <= 2 ? 'bg-amber-100 text-amber-700' : 'bg-blue-50 text-blue-600'}`}>
                            {freeSeats} {t('freeSeatsLabel', { free: '' }).trim() || 'slobodno'}
                        </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div
                            className={`h-1.5 rounded-full transition-all duration-500
                                ${isFull ? 'bg-green-500' : fillPercent >= 80 ? 'bg-amber-400' : 'bg-rose-400'}`}
                            style={{ width: `${fillPercent}%` }}
                        />
                    </div>
                </div>

                {/* Guest list */}
                <div className="px-4 pb-3 flex-1">
                    {table.reservedSeats > 0 ? (
                        <div className="flex flex-col gap-1.5">
                            {table.guests.map((guest, index) => {
                                // ── Obitelj ──────────────────────────────────────────────
                                if (guest.isFamilyGroup) {
                                    const attendingMembers = (guest.familyMembers || [])
                                        .filter(m => m.attending === true);

                                    return (
                                        <div
                                            key={index}
                                            className="flex flex-col bg-rose-50 border border-rose-100 rounded-lg px-2.5 py-1.5"
                                        >
                                            <div className="flex items-center gap-2">
                                                <FaUsers className="text-rose-500 text-xs flex-shrink-0" />
                                                <span className="text-sm text-gray-800 capitalize font-semibold truncate">
                                                    {capitalizeWords(guest.fullName)}
                                                </span>
                                                <span className="ml-auto flex-shrink-0 text-xs text-rose-500 font-medium">
                                                    {attendingMembers.length} {pluralizeOsoba(attendingMembers.length)}
                                                </span>
                                            </div>
                                            {attendingMembers.length > 0 && (
                                                <div className="flex flex-col gap-0.5 pl-5 mt-1">
                                                    {attendingMembers.map((member, mIdx) => (
                                                        <div
                                                            key={mIdx}
                                                            className="flex items-center gap-1.5 text-xs text-gray-600"
                                                        >
                                                            <FaUser className="text-gray-300 text-[10px] flex-shrink-0" />
                                                            <span className="capitalize truncate">
                                                                {capitalizeWords(member.name)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                }

                                // ── Običan gost ──────────────────────────────────────────
                                return (
                                    <div key={index} className="flex flex-col">
                                        <div className="flex items-center gap-2 py-1">
                                            <FaUser className="text-gray-400 text-xs flex-shrink-0" />
                                            <span className="text-sm text-gray-800 capitalize font-medium truncate">
                                                {capitalizeWords(guest.fullName)}
                                            </span>
                                            {guest.companions?.unregistered > 0 && (
                                                <span className="ml-auto flex-shrink-0 flex items-center gap-1 text-xs text-gray-500 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded-full">
                                                    <FaUserFriends className="text-xs" />
                                                    +{guest.companions.unregistered}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-4 text-gray-400">
                            <FaUsers className="text-2xl mb-1 opacity-30" />
                            <p className="text-sm">{t('noGuestsAtTable')}</p>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-end gap-2 bg-gray-50/50">
                    <button
                        onClick={() => handleEditTable(table)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition"
                    >
                        <FaPen className="text-xs" />
                        {t('edit') || 'Uredi'}
                    </button>
                    <button
                        onClick={() => handleLockTable(table._id)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition
                            ${table.isLocked
                                ? 'text-green-600 border-green-200 bg-white hover:bg-green-50'
                                : 'text-red-500 border-red-200 bg-white hover:bg-red-50'}`}
                    >
                        {table.isLocked ? <FaUnlock className="text-xs" /> : <FaLock className="text-xs" />}
                        {table.isLocked ? t('unlock') : t('lock')}
                    </button>
                    <button
                        onClick={() => handleDeleteTable(table._id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-500 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition"
                    >
                        <FaTrash className="text-xs" />
                    </button>
                </div>
            </div>
        );
    };

    const currentTables = activeTab === "public" ? publicTables : privateTables;

    // Ukupna statistika za aktivni tab
    const totalSeats = currentTables.reduce((acc, t) => acc + t.seats, 0);
    const totalReserved = currentTables.reduce((acc, t) => acc + t.reservedSeats, 0);

    return (
        <div className="pt-14 lg:pt-10 flex lg:ml-[300px] lg:mr-2">
            <div className="rounded-md w-full mt-2 px-2">

                {/* Naslov + dodaj */}
                <div className="flex justify-between lg:justify-around items-center mb-4">
                    <h1 className="text-3xl font-semibold">{t('seatingArrangement')}</h1>
                    <button
                        className="bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 shadow-lg shadow-rose-500/30 transition-all duration-300 text-white p-2 rounded-xl"
                        onClick={() => setActiveForm("addTable")}
                    >
                        <FaPlus className="text-xl" />
                    </button>
                </div>

                {/* Tabovi */}
                <div className="flex items-center gap-4 justify-around border border-gray-200 rounded-xl bg-white mb-4 shadow-sm">
                    {["public", "private"].map((tab) => {
                        const count = tab === "public" ? publicTables.length : privateTables.length;
                        const label = tab === "public" ? t('publicTables') : t('privateTables');
                        return (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`relative flex-1 p-3 flex items-center justify-center gap-2 text-base font-medium transition-colors
                                    ${activeTab === tab ? "text-gray-800" : "text-gray-400 hover:text-gray-600"}`}
                            >
                                {label}
                                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold
                                    ${activeTab === tab ? 'bg-rose-100 text-rose-600' : 'bg-gray-100 text-gray-400'}`}>
                                    {count}
                                </span>
                                {activeTab === tab && (
                                    <div className="absolute -bottom-[1px] left-4 right-4 h-[2px] bg-gray-700 rounded-full" />
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Statistika */}
                {currentTables.length > 0 && (
                    <div className="grid grid-cols-3 gap-3 mb-5">
                        <div className="bg-white border border-gray-200 rounded-xl p-3 text-center shadow-sm">
                            <p className="text-xl font-bold text-gray-800">{currentTables.length}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{t('tables') || 'Stolova'}</p>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-xl p-3 text-center shadow-sm">
                            <p className="text-xl font-bold text-rose-500">{totalReserved}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{t('reserved') || 'Rezervirano'}</p>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-xl p-3 text-center shadow-sm">
                            <p className="text-xl font-bold text-green-600">{totalSeats - totalReserved}</p>
                            <p className="text-xs text-gray-400 mt-0.5">Slobodno</p>
                        </div>
                    </div>
                )}

                {/* Prazno stanje */}
                {tables?.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                        <FaChair className="text-5xl mb-3 opacity-20" />
                        <h2 className="text-lg">{t("noTables")}</h2>
                    </div>
                )}

                {/* Grid stolova */}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 pb-10">
                    {currentTables.map((table) => (
                        <TableCard key={table._id} table={table} />
                    ))}
                </div>
            </div>

            {isEditModalOpen && (
                <EditTableModal
                    table={selectedTable}
                    onClose={() => setIsEditModalOpen(false)}
                    setNotificationMessage={setNotificationMessage}
                    setShowNotification={setShowNotification}
                    setErrorResponse={setErrorResponse}
                />
            )}

            <ConfirmModal
                isOpen={isModalOpen}
                onRequestClose={handleCloseModal}
                onConfirm={handleConfirmAction}
                message={message}
            />
        </div>
    );
};

export default Tables;
